import { ApolloQueryResult } from '@apollo/client';
import { TokenResponse } from '@react-oauth/google';
import React, { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { visitIQSettings } from '~/constants';
import { User } from '~/constants/graphqlTypes';
import { useAuthContext } from '~/contexts/AuthContext';
import { FilterDateField, MeQuery, OrganizationType, Permission, Scope, useMeQuery } from '~/types/api.graphql';
import { getUserOrganizationType } from '~/utils/User';

type Nullable<T> = T | null;

export interface SessionSettings {
  isOnboadingShown?: boolean;
  journeyGlobalAudience?: boolean;
  journeyPolygons?: boolean;
  isSideBarCollapsed?: boolean;
  isBusinessSubscriptionShown?: boolean;
  isBusinessExportWarningDontShowAgain?: boolean;
  uniquesPixelHitFilter?: FilterDateField;
}

interface Organization {
  id: string;
  parentOrganization?: Organization;
  hiddenPeopleFields?: string[];
}
interface SessionContextProps {
  me: Nullable<User>;
  refetchMeQuery: () => Promise<ApolloQueryResult<MeQuery>>;
  settings: Nullable<SessionSettings>;
  updateSettingsProperty: (property: keyof SessionSettings, value: any) => void;
  hasOrgWithPaidSubscription: boolean;
  hasOrgWithSubscriptionOverride: boolean;
  hasOrgWithBusinessDataEnabled: boolean;
  googleAuthToken?: TokenResponse;
  setGoogleAuthToken: (token: TokenResponse) => void;
  isOrgAdmin: boolean;
  organizationType: OrganizationType;
  orgMenuItemTypes: OrganizationType[];
  hiddenFieldsIntersection: string[];
  hasAudienceOnlyAccess: boolean;
  hasAccessToAudience: boolean;
  hasInviteToAnOrganization: boolean;
  loading: boolean;
  getHiddenPeopleFieldsForOrg: (org: Organization) => string[] | undefined;
  getHiddenPeopleFieldsForUserAndOrg: (org: Organization) => string[] | undefined;
}

interface SessionProviderProps {
  children: ReactNode;
}

const defaultValues: SessionContextProps = {
  me: null,
  refetchMeQuery: null,
  settings: null,
  updateSettingsProperty: null,
  hasOrgWithPaidSubscription: false,
  hasOrgWithSubscriptionOverride: false,
  hasOrgWithBusinessDataEnabled: false,
  googleAuthToken: null,
  setGoogleAuthToken: null,
  isOrgAdmin: false,
  organizationType: OrganizationType.Customer,
  orgMenuItemTypes: [],
  hiddenFieldsIntersection: [],
  hasAudienceOnlyAccess: false,
  hasAccessToAudience: false,
  loading: false,
  hasInviteToAnOrganization: false,
  getHiddenPeopleFieldsForOrg: null,
  getHiddenPeopleFieldsForUserAndOrg: null,
};

const SessionContext = createContext<SessionContextProps>(defaultValues);

export const SessionProvider: FC<SessionProviderProps> = ({ children }) => {
  const { authToken } = useAuthContext();

  const {
    data,
    refetch: refetchMeQuery,
    loading,
  } = useMeQuery({
    skip: !authToken,
  });

  const [settings, updateSettings] = useState<SessionSettings>(undefined);

  const [googleAuthToken, setGoogleAuthToken] = useState<TokenResponse>(undefined);

  const hasOrgWithPaidSubscription = useMemo(
    () => data?.me?.organizations?.some((org) => org?.paidSubscription),
    [data],
  );
  const hasOrgWithSubscriptionOverride = useMemo(
    () => data?.me?.organizations?.some((org) => org?.subscriptionOverride),
    [data],
  );
  const hasOrgWithBusinessDataEnabled = useMemo(
    () => data?.me?.organizations?.some((org) => org?.businessDataEnabled),
    [data],
  );

  const isOrgAdmin = useMemo(() => data?.me?.scopes?.includes(Scope.OrgsAdmin) || false, [data]);
  const isAudienceAdmin = useMemo(() => data?.me?.scopes?.includes(Scope.AudiencesAdmin) || false, [data]);

  const hasAudienceOnlyAccess = useMemo(() => {
    const canReadOrganization = (p: Permission) => [Permission.ReadOrganization, Permission.SuperOwner].includes(p);
    // Only check organizations length when data is loaded
    return (
      loading ||
      (!isOrgAdmin &&
        !data?.me?.userRoles?.some(
          (role) =>
            role.assignedRole.permissions.some(canReadOrganization) ||
            role.assignedRole.childOrgExtraPermissions?.some(canReadOrganization),
        ))
    );
  }, [data?.me?.userRoles, loading, isOrgAdmin]);

  const hasAccessToAudience = useMemo(() => {
    const canReadAudience = (p: Permission) => [Permission.ReadAudience, Permission.SuperOwner].includes(p);
    return (
      loading ||
      isAudienceAdmin ||
      data?.me?.userRoles?.some(
        (role) =>
          role.assignedRole.permissions.some(canReadAudience) ||
          role.assignedRole.childOrgExtraPermissions?.some(canReadAudience),
      )
    );
  }, [data?.me?.userRoles, isAudienceAdmin, loading]);

  const hasInviteToAnOrganization = useMemo(() => data?.me?.invites?.length > 0, [data?.me?.invites]);

  const hiddenFieldsIntersection = useMemo(() => {
    const userRoles = data?.me?.userRoles;

    if (!userRoles || userRoles.length === 0) {
      return [];
    }

    // Filter out roles without hiddenPeopleFields
    const rolesWithHiddenFields = userRoles.filter((role) => !!role.hiddenPeopleFields?.length);

    // If any role doesn't have hiddenPeopleFields, return empty array
    if (rolesWithHiddenFields.length !== userRoles.length) {
      return [];
    }

    // Calculate intersection
    return rolesWithHiddenFields.reduce((intersection, role, index) => {
      if (index === 0) {
        return [...(role.hiddenPeopleFields || [])];
      }
      return intersection.filter((field) => role.hiddenPeopleFields?.includes(field));
    }, [] as string[]);
  }, [data]);

  const getParent = (org: Organization) => org?.parentOrganization;

  const getHiddenPeopleFieldsForOrg = (org: Organization) => {
    // Collect all possible orgs to check (org, parent, parent's parent)
    const orgsToCheck = [org, getParent(org), getParent(getParent(org))].filter(Boolean);
    // Try to find a userRole with non-null hiddenPeopleFields for any of these org IDs
    const userRole = orgsToCheck
      .map(({ id: orgId }) => data?.me.userRoles.find((role) => role.organizationId === orgId))
      .find(Boolean);
    if (userRole?.hiddenPeopleFields) return userRole.hiddenPeopleFields;

    // Fallback: try to find non-null hiddenPeopleFields directly on the org or its parents
    const orgWithHiddenFields = orgsToCheck.find((o) => o?.hiddenPeopleFields);
    return orgWithHiddenFields?.hiddenPeopleFields || [];
  };

  const getHiddenPeopleFieldsForUserAndOrg = (org: Organization) => {
    const orgsToCheck = [org, getParent(org), getParent(getParent(org))].filter(Boolean);
    const mergedHiddenFields = new Set<string>();
    // Add hidden fields from the orgs
    orgsToCheck.forEach((o) => {
      if (o?.hiddenPeopleFields) {
        o.hiddenPeopleFields.forEach((field) => mergedHiddenFields.add(field));
      }
    });

    // Add hidden fields from user roles
    const userRole = orgsToCheck
      .map(({ id: orgId }) => data?.me.userRoles.find((role) => role.organizationId === orgId))
      .find(Boolean);
    if (userRole?.hiddenPeopleFields) {
      userRole.hiddenPeopleFields.forEach((field) => mergedHiddenFields.add(field));
    }

    return Array.from(mergedHiddenFields);
  };

  const organizationType: OrganizationType = useMemo(() => getUserOrganizationType(data?.me), [data]);

  const orgMenuItemTypes = useMemo(() => {
    if (organizationType === OrganizationType.Customer) return [];

    let types = [OrganizationType.WhiteLabel, OrganizationType.Agency, OrganizationType.Customer];
    if (organizationType !== OrganizationType.WhiteLabel) {
      types = types.filter((type) => type !== OrganizationType.WhiteLabel);
    }
    return types;
  }, [organizationType]);

  useEffect(() => {
    if (data?.me) {
      const settingsItem = localStorage.getItem(`${visitIQSettings}-${data.me.id}`);
      updateSettings(settingsItem ? (JSON.parse(settingsItem) as SessionSettings) : {});
    }
  }, [data?.me]);

  useEffect(() => {
    if (settings && data?.me) {
      if (!settings.uniquesPixelHitFilter) {
        settings.uniquesPixelHitFilter = FilterDateField.PixelFirstHitDate;
      }
      localStorage.setItem(`${visitIQSettings}-${data.me.id}`, JSON.stringify(settings));
    }
  }, [settings, data?.me]);

  const updateSettingsProperty = <Key extends keyof SessionSettings>(property: Key, value: SessionSettings[Key]) => {
    settings[property] = value;
    updateSettings(settings);
    localStorage.setItem(`${visitIQSettings}-${data.me.id}`, JSON.stringify(settings));
  };

  return (
    <SessionContext.Provider
      value={{
        me: data?.me || null,
        loading,
        refetchMeQuery,
        settings,
        isOrgAdmin,
        organizationType,
        orgMenuItemTypes,
        updateSettingsProperty,
        hasOrgWithPaidSubscription,
        hasOrgWithSubscriptionOverride,
        hasOrgWithBusinessDataEnabled,
        googleAuthToken,
        setGoogleAuthToken,
        hiddenFieldsIntersection,
        hasAudienceOnlyAccess,
        hasAccessToAudience,
        hasInviteToAnOrganization,
        getHiddenPeopleFieldsForOrg,
        getHiddenPeopleFieldsForUserAndOrg,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionContext: () => SessionContextProps = () => {
  return React.useContext(SessionContext);
};
